import {LinkLineStyle, LinkPosition, Widget, WidgetLink} from "@dv5/domain"

export const LINK_DASH_PATTERNS: Record<LinkLineStyle, string | undefined> = {
    solid: undefined,
    dashed: '8, 6',
    dotted: '2, 4',
}

export interface Point {
    x: number
    y: number
}

export interface ControlPoints {
    cp1: Point
    cp2: Point
}

export interface LinkPathResult {
    id: string
    path: string
    startPosition: LinkPosition
    endPosition: LinkPosition
    color?: string
    lineStyle: LinkLineStyle
}

class WidgetLinkService {
    getAnchorPoint(widget: Widget, position: LinkPosition): Point {
        const {left, top, width, height} = widget
        switch (position) {
            case 'top':
                return {x: left + width / 2, y: top}
            case 'bottom':
                return {x: left + width / 2, y: top + height}
            case 'left':
                return {x: left, y: top + height / 2}
            case 'right':
                return {x: left + width, y: top + height / 2}
        }
    }

    getControlPointOffset(startPos: LinkPosition, endPos: LinkPosition): ControlPoints {
        const offset = 50
        const getOffset = (pos: LinkPosition): Point => {
            switch (pos) {
                case 'top':
                    return {x: 0, y: -offset}
                case 'bottom':
                    return {x: 0, y: offset}
                case 'left':
                    return {x: -offset, y: 0}
                case 'right':
                    return {x: offset, y: 0}
            }
        }
        return {cp1: getOffset(startPos), cp2: getOffset(endPos)}
    }

    createBezierPath(start: Point, cp1: Point, cp2: Point, end: Point): string {
        return `M ${start.x} ${start.y} C ${start.x + cp1.x} ${start.y + cp1.y}, ${end.x + cp2.x} ${end.y + cp2.y}, ${end.x} ${end.y}`
    }

    computeLinkPath(link: WidgetLink, startWidget: Widget, endWidget: Widget): LinkPathResult | null {
        const startPosition = (link.data?.startPosition as LinkPosition) || 'right'
        const endPosition = (link.data?.endPosition as LinkPosition) || 'left'
        const lineStyle = (link.data?.lineStyle as LinkLineStyle) || 'solid'

        const start = this.getAnchorPoint(startWidget, startPosition)
        const end = this.getAnchorPoint(endWidget, endPosition)
        const offsets = this.getControlPointOffset(startPosition, endPosition)

        const path = this.createBezierPath(
            start,
            offsets.cp1,
            offsets.cp2,
            end
        )

        return {
            id: link.id,
            path,
            startPosition,
            endPosition,
            color: link.data?.color,
            lineStyle,
        }
    }

    computePreviewPath(sourceWidget: Widget, sourcePosition: LinkPosition, mousePosition: Point): string {
        const start = this.getAnchorPoint(sourceWidget, sourcePosition)
        const offsets = this.getControlPointOffset(sourcePosition, 'left')

        return this.createBezierPath(
            start,
            offsets.cp1,
            offsets.cp2,
            mousePosition
        )
    }
}

export const widgetLinkService = new WidgetLinkService()
